/**
 * 클래스 관리 클래스 상태
 * - 이용가능 : remainDays > 0 || (remainDays == 0 && endDateToday)
 * - 기간종료 : remainDays == null || (remainDays <= 0 && !endDateToday)
 * */

import { useNavigate } from 'react-router-dom';
import TextWinds from '../../../../components/common/TextWinds';
import ProgressDefault from './ProgressDefault';
import { calcPercent, changeTimestamp } from '../../../../api/utils/util-func';
import LearningCardShell, {
  CardEnrolment,
  ExtendButton,
  isEndDateToday,
} from './LearningCardShell';
import { Button } from '@material-tailwind/react';

export interface MyClassType {
  id: string; // 클래스 ID
  name: string; // 클래스 이름
  thumbnail?: string; // 썸네일
  enrolment: CardEnrolment;
  createdAt: string;
  updatedAt: string;
}

interface MyClassCardProps {
  data: MyClassType;
  linkTo: string;
  openExtend: (id?: string) => void;
}

type ClassStatus = '이용가능' | '기간종료';

/** 클래스 관리 - 목록 내 카드 */
const MyClassCard = ({ data, linkTo, openExtend }: MyClassCardProps) => {
  const navigate = useNavigate();
  const { enrolment } = data;
  const endDateToday = isEndDateToday(enrolment?.endDate);

  // 클래스 상태 확인
  const getStatus = (): ClassStatus => {
    if (enrolment?.remainDays === null) {
      return '기간종료';
    }
    if (Number(enrolment?.remainDays) <= 0 && !endDateToday) {
      return '기간종료';
    }
    return '이용가능';
  };

  const status = getStatus();

  // '기간 연장하기' 버튼 노출 여부 (클래스 상태 기준으로 판정)
  // 1. 기간종료 : 항상 노출
  // 2. 이용가능 : 만료 임박(30일 이하)일 때만 연장 노출
  const canExtend = () => {
    if (status === '기간종료') {
      return true;
    }
    return Number(enrolment?.remainDays) <= 30;
  };

  // 버튼 클릭 - 이용 가능하면 대시보드로 이동, 아니면 연장 모달
  const handleActionClick = () => {
    if (status === '이용가능') {
      navigate(`${linkTo}`);
    } else {
      openExtend(data.id);
    }
  };

  // '기간 연장하기' 버튼 클릭 - 연장 모달
  const handleExtendClick = () => {
    openExtend(data.id);
  };

  // 클래스 기간 존재 여부 확인 (endDate가 있고, remainDays가 null이 아닌 경우)
  const hasPeriod = enrolment?.endDate && enrolment?.remainDays !== null;

  return (
    <LearningCardShell thumbnail={data.thumbnail} name={data.name}>
      {hasPeriod ? (
        <div className="flex flex-col gap-y-2">
          <ProgressDefault
            percentage={calcPercent(
              new Date(enrolment.startDate),
              new Date(enrolment.endDate),
            )}
          />
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <TextWinds
              type="content_body4"
              color="gray7"
              className="whitespace-nowrap"
            >
              {Number(enrolment.remainDays) > 0
                ? `${enrolment.remainDays}일 남음 `
                : endDateToday
                  ? '오늘까지 '
                  : '기간 종료 '}
              ({changeTimestamp(enrolment.endDate)}까지)
            </TextWinds>
            {canExtend() && <ExtendButton onClick={handleExtendClick} />}
          </div>
        </div>
      ) : (
        // !hasPeriod: endDate가 없거나, remainDays가 null인 경우
        <div className="flex flex-col gap-y-2">
          <ProgressDefault percentage={0} />

          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <TextWinds
              type="content_body4"
              color="gray7"
              className="whitespace-nowrap"
            >
              기간 종료
            </TextWinds>
            <ExtendButton onClick={handleExtendClick} />
          </div>
        </div>
      )}

      <Button
        className={`rounded p-2 shadow-none hover:shadow-none ${
          status === '기간종료'
            ? 'bg-gray5 hover:bg-gray6'
            : 'bg-purple5 hover:bg-purple6'
        }`}
        disabled={status === '기간종료'}
        onClick={handleActionClick}
      >
        <TextWinds type="button" color="white">
          클래스 입장하기
        </TextWinds>
      </Button>
    </LearningCardShell>
  );
};

export default MyClassCard;
